import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { getAccessibleBadgeStyles } from 'src/app/utils/utils.function';

/**
 * TicketDetailsTabComponent
 *
 * Display component for ticket metadata and details.
 * Shows: ticket type, scopes, customer, contact, vendor, subcontractor, status, priority, dates, etc.
 *
 * @selector app-ticket-details-tab
 * @changeDetection OnPush
 */
@Component({
    selector: 'app-ticket-details-tab',
    templateUrl: './ticket-details-tab.component.html',
    styleUrls: ['./ticket-details-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TicketDetailsTabComponent {
  // Form and mode
  @Input() ticketForm?: UntypedFormGroup;
  @Input() mode: 'create' | 'edit' | 'view' = 'view';

  // Status and priority
  @Input() status?: string;
  @Input() statuses?: any[];
  @Input() statusId?: number;
  @Input() priority?: string;

  // Dates
  @Input() dueDate?: string;
  /** Time Planning: "Start Task On" (start_date) and "Finish" (date_finished). */
  @Input() startDate?: string;
  @Input() endDate?: string;
  @Input() createdDate?: string;

  // Stage and assignee
  @Input() stage?: string;
  @Input() assignee?: string;

  // Ticket-specific fields
  @Input() ticketType?: string;
  @Input() customer?: string;
  @Input() vendor?: string;
  @Input() subcontractor?: string;
  @Input() responsibleParty?: string;
  @Input() scopes?: any[];

  // Legacy support - can be removed once all components updated
  @Input() ticketData: any = {};

  /**
   * Get the background color for the status badge
   * @returns Hex color code from status object, or default gray
   */
  getStatusColor(): string {
    if (!this.statuses || !this.statusId) {
      return '#6b7280'; // Default gray if no status data
    }
    const status = this.statuses.find(s => s.id === this.statusId);
    return status?.color || '#6b7280';
  }

  /**
   * Scope chip fill + text. Same bug as the status badges: it used to paint
   * the raw scope colour with hardcoded white text, unreadable for pale
   * scope colours. Reuses the same WCAG-contrast rule the status pills use.
   */
  getScopeChipStyles(scope: any): { [key: string]: string } {
    const accessible = getAccessibleBadgeStyles(scope?.color || '');
    return {
      'background-color': accessible.backgroundColor,
      'color': accessible.color,
    };
  }
}
